'use client';
import { gsap } from 'gsap';
import { memo, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { Text, VStack } from '@chakra-ui/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import CooperationCard from 'entities/CooperationCard';
import BlockHeader from 'shared/BlockHeader';
import ButtonGoTo from 'shared/ButtonGoTo';

import { defaultCardsData, defaultCardsStyles } from 'constants/Cooperations';

gsap.registerPlugin(useGSAP, ScrollTrigger);

ScrollTrigger.config({
  ignoreMobileResize: true,
});

const Cooperation = ({
  title,
  subtitle = null,
  titleProps = null,
  cardsData = defaultCardsData,
  cardsStyles = defaultCardsStyles,
  className = 'default',
  showButton = true,
  metricName = null,
}) => {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      const gsapInit = context => {
        const { isLaptop, isTablet, isMobile } = context.conditions;

        const getOffsetValues = i => {
          if (isMobile) {
            return i === 1 ? i * 60 : i * 60 - (i - 1) * 20;
          }

          if (isTablet) {
            return i === 1 ? i * 90 : i * 90 - (i - 1) * 40;
          }

          if (isLaptop) {
            return i === 1 ? i * 100 : i * 100 - (i - 1) * 40;
          }

          return i === 1 ? i * 125 : i * 125 - (i - 1) * 20;
        };

        const cards = gsap.utils.toArray(`.card-${className}`);
        cards.forEach((card, i) => {
          ScrollTrigger.create({
            trigger: card,
            pin: true,
            pinSpacing: false,
            scrub: 1,
            start: () => `top ${getOffsetValues(i + 1)}px`,
            end: () => `top ${getOffsetValues(cards.length)}px`,
            // @ts-ignore
            endTrigger: cards.at(-1),
            onLeave: () => gsap.set(card, { left: '0' }),
          });
        });
      };

      mm.add(
        {
          isDesktop: '(min-width: 992px)',
          isLaptop: '(min-width: 742px) and (max-width: 991px)',
          isTablet: '(min-width: 656px) and (max-width: 741px)',
          isMobile: '(max-width: 655px)',
        },
        context => gsapInit(context),
      );
    },
    { scope: containerRef.current },
  );

  return (
    <VStack ref={containerRef} w="full" gap={{ laptop: '40px', tablet: '24px', mobile: '20px' }}>
      <VStack w="full" gap={{ laptop: '40px', tablet: '24px', mobile: '20px' }} align="start">
        <VStack w="full" gap="20px" align="start">
          <BlockHeader {...titleProps}>
            {title ?? (
              <>
                Выберите подходящую <br /> модель сотрудничества
              </>
            )}
          </BlockHeader>

          {subtitle && (
            <Text textStyle="h1" maxW={{ laptop: '800px' }}>
              {subtitle}
            </Text>
          )}
        </VStack>

        <VStack w="full" gap="20px" className="cards">
          {cardsData?.map((val, index) => (
            <CooperationCard
              key={index}
              pos={val.pos || index + 1}
              max={val.max || cardsData.length}
              // @ts-ignore
              tag={val.tag || val.title}
              className={`card-${className}`}
              {...cardsStyles[val.pos || index + 1]}
              {...val}
            />
          ))}
        </VStack>
      </VStack>

      {showButton && (
        <ButtonGoTo className={className} ymName={metricName} size="secondary" w="full" />
      )}
    </VStack>
  );
};

export default memo(Cooperation);
